import { useState } from 'react';

const leaders = [
  {
    name: 'Rajesh Kapoor',
    role: 'Founder & CEO',
    bio: 'Over 20 years of experience leading enterprise network transformations and data center modernization projects for Fortune 500 companies.',
    certifications: ['CCIE R&S', 'CCIE DC', 'VCP-DCV', 'AWS Solutions Architect'],
    expertise: ['Enterprise Architecture', 'SDWAN & SASE Strategy', 'Digital Transformation'],
    linkedin: '#',
  },
  {
    name: 'Priya Sharma',
    role: 'VP of Engineering',
    bio: 'Former principal engineer at a leading cloud provider with deep expertise in VMware Cloud Foundation, NSX, and large-scale virtualization deployments.',
    certifications: ['VCAP-DCV', 'VCAP-NV', 'VCP-CMA', 'CCNP DC'],
    expertise: ['VMware Cloud Foundation', 'NSX Architecture', 'VCF Automation'],
    linkedin: '#',
  },
  {
    name: 'Arjun Mehta',
    role: 'Director of Network Solutions',
    bio: '15+ years designing and deploying complex data center networks using Cisco ACI, NDFC, and Arista platforms across multi-site enterprise environments.',
    certifications: ['CCIE DC', 'ACE-A', 'Infoblox Certified', 'F5 Certified'],
    expertise: ['Cisco ACI & NDFC', 'Arista Data Centers', 'DDI Solutions'],
    linkedin: '#',
  },
  {
    name: 'Ananya Desai',
    role: 'Head of AI/ML Practice',
    bio: 'PhD in Machine Learning with extensive experience building production-grade Gen-AI, RAG, and Agentic-AI solutions for enterprise clients worldwide.',
    certifications: ['PhD ML', 'Google Cloud ML Engineer', 'AWS ML Specialty'],
    expertise: ['Gen-AI & RAG', 'Agentic-AI Systems', 'ML Ops & Deployment'],
    linkedin: '#',
  },
  {
    name: 'Vikram Singh',
    role: 'Principal Consultant',
    bio: 'Specialist in automated network assessments and cloud infrastructure consulting, having delivered 100+ successful migration projects across industries.',
    certifications: ['CCIE Enterprise', 'Azure Solutions Architect', 'PCNSE'],
    expertise: ['Network Automation', 'Cloud Migration', 'Security Assessment'],
    linkedin: '#',
  },
  {
    name: 'Meera Patel',
    role: 'Lead VMware Architect',
    bio: 'Expert in HCX migrations, Azure VMware Solution deployments, and VMware Live Recovery implementations with a track record of zero-downtime transitions.',
    certifications: ['VCAP-DCV', 'VCP-NV', 'Azure Expert', 'ITIL v4'],
    expertise: ['HCX Migration', 'AVS Deployments', 'Disaster Recovery'],
    linkedin: '#',
  },
];

const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('');
};

const avatarColors = [
  'from-teal-500 to-cyan-600',
  'from-cyan-500 to-teal-600',
  'from-teal-600 to-emerald-500',
  'from-emerald-500 to-teal-500',
  'from-cyan-600 to-teal-500',
  'from-teal-500 to-cyan-500',
];

export default function Team() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section id="team" className="py-24 bg-white relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-teal-50 rounded-full -translate-y-1/2 translate-x-1/2 opacity-60"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-50 rounded-full translate-y-1/2 -translate-x-1/2 opacity-60"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600 text-sm font-semibold tracking-wider uppercase mb-3">
            OUR LEADERSHIP
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Meet the Experts Behind Your Success
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A team of certified professionals with decades of combined experience in network engineering, VMware solutions, and AI/ML innovation
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {[
            { value: '100+', label: 'Years Combined Experience', icon: 'ri-time-line' },
            { value: '50+', label: 'Industry Certifications', icon: 'ri-award-line' },
            { value: '100+', label: 'Projects Delivered', icon: 'ri-rocket-line' },
            { value: '98%', label: 'Client Satisfaction', icon: 'ri-heart-line' },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 text-center border border-gray-100 shadow-sm"
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-teal-50 rounded-lg">
                <i className={`${stat.icon} text-xl text-teal-600`}></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {leaders.map((member, index) => (
            <div
              key={index}
              className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-400 cursor-pointer"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Header with initials avatar */}
              <div className="p-6 pb-4 flex items-center gap-4">
                <div className={`w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br ${avatarColors[index % avatarColors.length]} text-white text-xl font-bold shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  {getInitials(member.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                    <a
                      href={member.linkedin}
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-gray-400 hover:bg-teal-500 hover:text-white transition-colors cursor-pointer shrink-0"
                    >
                      <i className="ri-linkedin-fill text-base"></i>
                    </a>
                  </div>
                  <p className="text-teal-600 text-sm font-medium">{member.role}</p>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 pb-6">
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  {member.bio}
                </p>

                {/* Expertise Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {member.expertise.map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                {/* Certifications */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-medal-line text-sm text-teal-600"></i>
                    </div>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1.5">
                      Certifications
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {member.certifications.map((cert, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md"
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">
            Want to work with our experts on your next project?
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-cyan-700 transition-all cursor-pointer whitespace-nowrap"
          >
            <span>Schedule a Consultation</span>
            <div className="w-5 h-5 flex items-center justify-center ml-2">
              <i className="ri-arrow-right-line text-lg"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
